// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BreakPriceDto } from "@insite/client-framework/Types/ApiModels";

/**
 * Get the appropriate quote line break price based on quantity ordered and break prices.
 *
 * @param breakPrices - An array of BreakPriceDto representing quantity break prices.
 * @param qtyOrdered - The quantity ordered.
 * @param fallbackPrice - Optional fallback price if break prices are not available.
 * @returns The calculated break price for the given quantity ordered.
 */
const getQuoteLineBreakPrice = (
    breakPrices: BreakPriceDto[] | null,
    qtyOrdered: number,
    fallbackPrice?: number,
): number => {
    // If breakPrices are not available and a fallback price is provided, return the fallback price.
    if (!breakPrices) {
        if (fallbackPrice || fallbackPrice === 0) {
            return fallbackPrice!;
        }

        return 0;
    }

    // If quantity ordered is not a valid number, return 0.
    if (Number.isNaN(qtyOrdered)) {
        return 0;
    }

    // Initialize the break price to 0.
    let breakPrice = 0;

    // Iterate through each break price.
    for (let i = 0; i < breakPrices!.length; i++) {
        // Retrieve the current and next break prices.
        const currentPrice = breakPrices![i];
        const nextPrice = breakPrices![i + 1];

        // Check if the quantity ordered matches the current break quantity or falls within the range of the current and next break quantities.
        const isQtyMatching = qtyOrdered === currentPrice.breakQty;
        const isQtyInRange = qtyOrdered > currentPrice.breakQty && (!nextPrice || qtyOrdered < nextPrice.breakQty);

        if (isQtyMatching || isQtyInRange) {
            // Set the break price and break out of the loop.
            breakPrice = currentPrice.breakPrice;
            break;
        }
    }

    return breakPrice;
};

export default getQuoteLineBreakPrice;
